/**
 * What the "one more detail for <app>" form does (Integrations part 1, PV13, 2026-10-11).
 *
 * Moved verbatim out of src/services/composio/panel/SetupFieldsDialog.tsx: when the server sends help, the
 * pathway is asked first and the fields show only for a pathway that can proceed; choosing a pathway clears a
 * note that was showing; a typed value is handed to the host as the next prompt. The form draws, on the
 * Integrations page and for the app-wide setup host alike; the new design's will read the same hook.
 *
 * Its frame is handed one steady close that always calls the host's latest cancel. The frame sets itself up again,
 * and takes the focus back to its card, whenever its close changes, and both hosts hand a new cancel on every render,
 * which is every typed character: the person could type one character before the focus left the field (found in
 * Integrations part 1, 2026-10-11).
 */
import { useCallback, useEffect, useRef } from 'react';
import type { ConnectInitField, ConnectToolkitHelp } from '../../services/composio/ComposioClient';

export interface SetupPromptState {
  slug: string;
  fields: ConnectInitField[];
  values: Record<string, string>;
  note?: string;
  submitting?: boolean;
  /** Account-type triage (2026-08-17): when present, the dialog opens with
   *  a chooser; fields render only for the chosen pathway when it can
   *  actually proceed (showFields). */
  help?: ConnectToolkitHelp;
  pathwayId?: string;
}

/** The form's line when its sign-in on Prest0n's browser did not start (D9: failure copy). */
// copy: setup_form_link_failed
export const LINK_IN_BROWSER_FAILED_LINE = "the sign-in window didn't open — try again";

export function useSetupFieldsDialog({ prompt, onChange, onCancel, inConversation = false }: {
  prompt: SetupPromptState;
  onChange: (next: SetupPromptState) => void;
  onCancel: () => void;
  /** The form is open over a conversation (the chat's setup host): a sign-in it starts opens beside that conversation,
   *  never in a thread of its own, so the conversation carries on when it finishes (browserSignInStart inConversation). */
  inConversation?: boolean;
}) {
  const pathway = prompt.help?.pathways.find((p) => p.id === prompt.pathwayId) || null;
  const showFields = !prompt.help || Boolean(pathway?.showFields);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const close = useCallback(() => cancelRef.current(), []);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const promptRef = useRef(prompt);
  promptRef.current = prompt;
  // Whether the form is still drawn: a start that answers after the person closed it never opens it again.
  const drawnRef = useRef(true);
  useEffect(() => {
    drawnRef.current = true;
    return () => {
      drawnRef.current = false;
    };
  }, []);
  // The sign-in start in flight (0 when none). While it runs the pathway stays as pressed, and only its own answer
  // settles the form.
  const linkingRef = useRef(0);
  const attemptsRef = useRef(0);
  // tri_mvoonxkl_668d88317e: a pathway that signs in on Prest0n's browser (a personal WhatsApp, or the Business app on a
  // phone) is the person's own click away, through the one door every surface uses (browserSignInStart.ts), on the
  // app's own page. The form waits for the door: it closes once the sign-in has started, and says so when it did not
  // (the reviewer's finding: a start that failed after the form closed said nothing). The door loads only on the click,
  // so the page's first download carries none of it.
  const browser = pathway?.browserSignIn;
  const linkInBrowser = browser && typeof browser.appName === 'string' && browser.appName.trim()
    && typeof browser.signInUrl === 'string' && /^https:\/\//.test(browser.signInUrl)
    ? () => {
      const asked = promptRef.current;
      if (asked.submitting || linkingRef.current) return;
      const attempt = ++attemptsRef.current;
      linkingRef.current = attempt;
      changeRef.current({ ...asked, submitting: true, note: undefined });
      const settle = (started: boolean) => {
        if (linkingRef.current !== attempt) return;
        linkingRef.current = 0;
        // Closed meanwhile, or the host opened another app's form: nothing to settle.
        const now = promptRef.current;
        if (!drawnRef.current || now.slug !== asked.slug) return;
        if (started) cancelRef.current();
        else changeRef.current({ ...now, submitting: false, note: LINK_IN_BROWSER_FAILED_LINE });
      };
      void import('../../services/composio/browserSignInStart')
        .then(({ startBrowserSignIn }) => startBrowserSignIn({
          appName: browser.appName.trim(),
          signInUrl: browser.signInUrl,
          surface: 'setup-form',
          toolkitSlug: asked.slug,
          ...(inConversation ? { inConversation: true } : {}),
        }))
        .then((out) => settle(out.route !== 'none'), () => settle(false));
    }
    : null;
  return {
    pathway,
    showFields,
    /** The pathway's own sign-in on Prest0n's browser, started on the person's click; null when it has none. */
    linkInBrowser,
    /** The frame's close (Escape, the backdrop): the same function every render, calling the host's latest cancel. */
    close,
    choosePathway: (pathwayId: string) => {
      if (linkingRef.current) return;
      onChange({ ...prompt, pathwayId, note: undefined });
    },
    setValue: (name: string, value: string) => onChange({ ...prompt, values: { ...prompt.values, [name]: value } }),
  };
}
